/* 
	 NEW! FlexiItem

   The Problem: Most all other 'item' applications right now (7/28/2026) setup their own logic for:
   1. Fetching: Reading the Record including nested relationships and datetime formatting.
   2. Updating: Client side validation
   3. Server Side Validation - Each page seems to have it's own controller to handle edge cases.
   4. DOM Elements and Behavior - A lot of grids, tables, inputs, all being rewritten over and over, per controller.
   5. Menu System including the state of a save button, audit buttons, restore button, custom functional buttons.
   6. History and Rollback System
   7. Restore Deleted - Hardly any systems support loading/restoring records.
   8. Back Button logic - Most controller aren't compatible with being a child view of another controller, a desired feature.


   The Solution: Send configuration to dedicated universal functions (FlexiItemLoader, FlexiItemContainer, FlexiItemInput, FlexiItemMenu...)
   1. Fetching: Declare configuration with 'nestedrelationships' and 'datetimes' configuration
   2. Updating: Setup 'rules' for each item in itemfields configuration (column key values) to be digested by FlexiItemValidation on changes
   3. Server Side Validation: Setup new specialized validation for each table in FlexiItemController.
   4. DOM Elements will now be importable, reusable chunks that have logic built around itemfields configuration for behavior changes (validation errors).
   5. Menu will be converted to a reusable FlexiItemMenu that can be sticky if configured and has reusable logic.
   6. History can be loaded by flexrecorddeltas, walking history backward (non-editmode!), highlighting changes with new functional components, ability to restore record.
   7. Restoring records should be more straightforward with the new history system. There will still need to be uniqueness 
      configuration before a user can restore a record, it may have already been supplanted by a duplicate!
   8. Back Button should be configured by using props on this controller. (affects how we derive DB ID!)


   HOW THIS DIFFERS FROM FLEXIGRID:
   FlexiGrid is a highly structured, predictable application that acts the same across many tables. It involves a very large amount of inputs and
   rendered values. It saves a lot of time to standardize grids for basic viewing, sorting, searching, exports, and basic editing.  
 
   However 'Item' views (Products, Inventory Items, Orders) have highly customized layouts and validation logic. Leaving data at localhost.itemdata
   allows it to be accessible for custom functions. So for now, I'm leaving 'Item' views flat as before.
*/


//React & Friends
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import LoadItem from '../common/FlexiItem/FlexiItemLoader';
import FlexiItemTopMenu from '../common/FlexiItem/FlexiItemTopMenu';
import FlexiItemHeader from '../common/FlexiItem/FlexiItemHeader';
import FlexiItemContent from '../common/FlexiItem/FlexiItemContent';

//Unique ID generator
import { v4 as uuidv4 } from 'uuid';

//Contexts
import { AppContext } from "../Auth/contexts/AppContext"

//Redux Features
import { useSelector, useDispatch } from 'react-redux'; //Controls active menu item and others.
import {
	setCurrentMenuSection,
	setCurrentMenuItem
} from '../../features/mainmenu/mainmenuSlice';


//CSS Styles
//import { useTheme } from '@mui/material/styles'; //Only if needed. Access theme variables with: const theme = useTheme(); Add custom properties to theme in ui/theme/index.js.
import flexstyles from '../../css/FlexCss';
import useClasses from '../../ui/useClasses';

//Datetime formatting
import dayjs from 'dayjs'; //Used with new datetimepickers

//MaterialUI
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';

//Icons for Custom Buttons:
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Select } from '@mui/material';




const BoilerplateFlexiItem = ({isChild,	itemid,	viewmode, backlinkurl, backlinktext, SetView, UpdateState}) => {
	/* CSS and Media Queries */
	const classes = useClasses(flexstyles);

	/* App Context - Allows userPerms, userRole, and others to be used*/
	const appContext = useContext(AppContext);
	const { userPerms, userRole } = appContext;
	const history = useHistory();

	const dispatch = useDispatch();

	/* ##########################  Configuration  ########################## */
	//Perms - Be sure endpoints are secure because this certainly isn't. These are for perm driven views that child <FlexiGrid> will digest and use, not the perms on data.
	var perms = {
		createPerm: (userPerms['createDemoData'] === 1 ? true : false),
		readPerm: (userPerms['readDemoData'] === 1 ? true : false),
		updatePerm: (userPerms['updateDemoData'] === 1 ? true : false),
		deletePerm: (userPerms['deleteDemoData'] === 1 ? true : false),
		admin:(userRole === "admin" ? true : false)
	}

	//URL Param for ID
	//Check Props before using params. This controller may be called from a parent controller, so the ID may come from there. (such as a grid view)
	let { id } = useParams(); //Get parameter from URL - In table views, the route isn't configured to take an ID, which is OK because we won't need it on child view.
	if (!id && isChild){
		id = itemid;
	}
	console.log("This id: "+id);


	/* ##########################  DATA MUTATION  ########################## */
	// //   This function allows us to mutate data after it is received from the database, but before it is rendered on the grid or exported.
	// const MutateData = (dbdata) => {
	// 	//Mutate each item in dbdata as needed.
	// 	for (var i=0; i<dbdata.length; i++){
	// 		//Example: Add Margin calculation
	// 		dbdata[i]["NewMargin"] = (parseFloat(dbdata[i]["Price"]) - parseFloat(dbdata[i]["Cost"])).toFixed(2);
	// 	}
	// 	return dbdata;
	// }
	// //An example of mutation specifically for exports. This allows you to have different data shown on screen vs exported.
	// const ExportMutateData = (dbdata) => {
	// 	//Mutate each item in dbdata as needed.
	// 	for (var i=0; i<dbdata.length; i++){
	// 		//Example: Add Margin calculation
	// 		dbdata[i]["NewNewMargin"] = (parseFloat(dbdata[i]["Price"]) - parseFloat(dbdata[i]["Cost"])+500).toFixed(2);
	// 	}
	// 	return dbdata;
	// }



	/* ##########################  CUSTOM FUNCTIONS  ########################## */
	const CalculateMargin = ({localstate}) =>{
		localstate.itemdata.Margin = (parseFloat(localstate.itemdata.Price) - parseFloat(localstate.itemdata.Cost)).toFixed(2);
	}


	

	//Configuration
	const configuration = {

		//Menu Setup
		menuitem: "/boilerplateflexigrid",
		menusection: "Martin's Section",
		title: "Boilerplate Item", //Tab Title
		//HeaderConstruction
		headerprepend:"Boilerplate Item: ", //string or null
		headervalue: 'Name', //db key for value you want to get or null.
		headerappend:"", //string or null

		//Page Options
		maxwidth:"1200px",
		defaulteditmode: true, //Is the page immediatley


		//Data Options
		fetchapiendpoint: "/flexiitem/getitem", //  'flexiitem/' or custom endpoint
		updateapiendpoint: "/flexiitem/update",
		dbmodel: "basictable", //or flexproduct, flexorder, etc	
		dbkey: "ID", //Used as db key for Reading, Updating, Deleting
		dbkeyvalue: id,
		nestedrelationships: [
			'creationuser', //CreatedByUserID
			'updateuser',	//UpdatedByUserID
			'deletionuser', //DeletedByUserID
			'product',
			'basictablechildren'
		],
		//These are things like tags, conditions, etc.
		editablechildren:[
			'basictablechildren'
		],
		childitemurl: "/boilerplateflexiitem/", //When calling SetView or clicking 'ADD ITEM' button, goes to this url with the ID of created item. This exists to be able to make one item after the other if we choose.
		parentitemurl: "/boilerplateflexigrid/", //When calling SetView from the child, this helps link us back to the appropriate grid. Or when we delete something!
		//parentitemurl: "/", //Conversely, you can set it to go home if there really isn't any parent to speak of.
		shortposttimeout: 20000, //Posts don't really need to be short, but they shouldn't be really long either. Let's set a short post to 20 seconds just in case the server gets busy. 20000
		longposttimeout: 60000,
		//DB Support items (try to add supports to DB tables when it's helpful)
		softdeletesupport: true,
		creationusersupport: true,
		updateusersupport: true,
		deleteusersupport: true,
		historysupport: true, //THIS IS A PART OF NEW FLEXRECORDDELTAS

		//Perms - So we're just assuming anything could happen in a browser. Any permvalue could be sent to a controller, any data, even validaton broken. Let server-side perms sort that out by dbmodel. Either the user can or can't.
		//So let's just establish perms in configuration.<permtype> and go with it!
		createPerm: (userPerms['createDemoData'] === 1 ? true : false),
		readPerm: (userPerms['readDemoData'] === 1 ? true : false),
		updatePerm: (userPerms['updateDemoData'] === 1 ? true : false),
		deletePerm: (userPerms['deleteDemoData'] === 1 ? true : false),
		auditPerm: (userPerms['auditDemoData'] === 1 ? true : false),  //Allows for traversal of History.


		//Mutations: 
		//    Useful for derived values that are usually rendered, but not for edits. Things such as FullName or TotalCharges which might change based on a change of a nested value.
		//    They are calculated client-side after data load and can be used for view. This helps keep our data loader clean of custom logic.
		//    Assign a Fn or null.

		// mutatedata:MutateData,
		// exportmutatedata:ExportMutateData,
		mutatedata:null,
		exportmutatedata:null,


		//Datetimes to handle server-side. Loader will 
		datetimes: [
			'DateTime',
			'created_at',
			'updated_at',
		],

		//Float values look nicer when parsed
		floats: [
			'Cost'
		],


		/* ##########################  Page Configuration  ########################## */

		topmenubar:{
			render:true,
			backbutton:{
				render:true, //Back behavior will depend on 
				nonchildlinkurl:"/boilerplateflexigrid",
				nonchildlinktext:"Go to Boilerplate Table"
			},
			savebutton:{
				render:true,
				text:"Save Changes", //Save space by making shorter
			},
			printbutton:{
				render:true,
				text: "Print Record",
			},
			//Move Delete Button Somewhere Else!
			deletebutton:{
				render:false,
				text: "Delete Record",
				confirmationtext:"Confirm deletion of record: ", //Customize at your leisure
			},
			statusbar:true,


			custombuttons:[
				{
					render:true,
					text:"Custom Btn 1",
					icon: <RestartAltIcon sx={{
						color: "#222", fontSize: "1.2em", // match text size
						verticalAlign: "middle", // helps on some browsers
						display: "inline-block",
					}}>
					</RestartAltIcon>,
				}
			]
		},

		//Fields and validation, together at last.
		contentelements:[
			{
				rendertype:"section",
				title: "Sample Section 1",
				description: "Sample description",
				//Sections should always have children
				children:[
					{
						rendertype:"gridcontainer",
						children:[
							{
								rendertype:"griditem",
								size:{
									xs:12,
									sm:12,
									md:6,
									lg:6
								},
								children:[
									//KV types are "Key Value" types where we commonly setup a left side label and a right side input. 
									{
										rendertype:"kvstaticinputtext",
										dbfield:"ID",
										label:"ID:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									//Input serial allows creation users to create a new serial number (should validate unique) (TODO)
									//Avoid mutation even by update users! Only admins and people that can create should be able to make a serialnumber.
									{
										rendertype:"kvinputserial",
										dbfield:"SerialNumber",
										label:"Serial Number:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										validation:{
											type:"text",
											maxlength:"100",
											minlength:"1"
										}
									},
									{
										rendertype:"kvinputtext",
										dbfield:"Name",
										label:"Name:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										validation:{
											type:"text",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype:"kvstaticselect",
										dbfield:"Name",
										label:"Name (select):",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"Adam", value:"Adam"},
											{ optionlabel:"James", value: "James"},
											{ optionlabel:"Mike", value:"Mike"}
										],
										validation:{
											type:"select",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype:"kvinputnumber",
										dbfield:"Cost",
										label:"Cost:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										step:"0.01",
										validation:{
											type:"number",
											min:0,
											max:1000000000000
										},
										postChangeHandler:CalculateMargin
									},
									{
										rendertype:"kvinputnumber",
										dbfield:"Price",
										label:"Price:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										step:"0.01",
										validation:{
											type:"number",
											min:0,
											max:1000000000000
										},
										postChangeHandler:CalculateMargin
									},
									{
										rendertype:"kvstaticinputtext",
										dbfield:"Margin",
										label:"Margin:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										step:"0.01",
										validation:{
											type:"number",
											min:0,
											max:1000000000000
										}
									},
									{
										rendertype:"kvinputnumber",
										dbfield:"PositiveInteger",
										label:"Positive Integer:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										step:"1",
										validation:{
											type:"number",
											min:0,
											max:1000000000000
										},
									},
									//DateTime
									{
										rendertype:"kvstaticdatetime",
										dbfield:"DateTime",
										label:"Static DateTime:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvinputdatetime",
										dbfield:"DateTime",
										label:"DateTime Picker:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									//Date
									{
										rendertype:"kvstaticdate",
										dbfield:"Date",
										label:"Static Date:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvinputdate",
										dbfield:"Date",
										label:"Date Picker:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									//Creation Date
									{
										rendertype:"kvstaticdatetime",
										dbfield:"created_at",
										label:"Creation Date:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									//Creation User
									{
										rendertype:"kvcreationuser",
										dbfield:"creationuser",
										label:"Created By:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvstaticdatetime",
										dbfield:"updated_at",
										label:"Update Date:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvupdateuser",
										dbfield:"updateuser",
										label:"Updated By:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvproductbyid", //We don't store Product Name inside records. We store the Product ID. The input shows the product name from the relationship, but a function updates the ProductID.
										dbfield:"product",
										label:"Product:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvinputtextarea",
										dbfield:"Notes",
										label:"Notes:",
										labelwidth:170,
										height:"45px",
										labeltextAlign:"right",
										valuetextAlign:"left",
										backgroundColor:"#EEE",
									},

								]
							},
							{
								rendertype:"griditem",
								size:{
									xs:12,
									sm:12,
									md:6,
									lg:6
								},
								children:[
									//Second Column
									{
										rendertype:"kvinputcheckbox",
										dbfield:"SomeBoolean",
										label:"Some Boolean (more):",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										validation:{
											type:"boolean"
										}
									},
									{
										rendertype:"kvstaticselect",
										dbfield:"SomeBoolean",
										label:"Boolean (select):",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										values:[
											//Build in any blank string options here:
											{ optionlabel:"-", value:null}, //DB may or may not like this.
											{ optionlabel:"Yes", value: 1},
											{ optionlabel:"No", value:0}
										],
										validation:{
											type:"select",
											maxlength:"10",
											minlength:"1"
										}
									},
									{
										rendertype:"kvinputbuttongroup",
										dbfield:"Grade",
										label:"Overall Grade:",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
										//New Concept: Render Requirements. Introduced with some asset types being "Laptop" and needing more grading inputs!
										renderrequirements:[
											{key:"SomeBoolean", values:[1]}, //Or perhaps values:["Laptop","AIO"] - both have LCDs to grade! Or-Able!
											//You can add more levels of requirements, but if it fails any level, it won't render.
											//{key:"Status", values:["Checked In", "Sold"]} //Example only
											//{key:"SerialNumber", values:[3334]}
										],
										values:[
											//Build in any blank string options here:
											{ optionlabel:"A", value:"A"},
											{ optionlabel:"B", value: "B"},
											{ optionlabel:"C", value:"C"},
											{ optionlabel:"Repair", value:"Repair"},
											{ optionlabel:"Scrap", value:"Scrap"},
										],
										validation:{
											type:"text"
										}
									},
									//Special header to help separate some values! Useful for children.
									{
										rendertype:"kvheader",
										dbfield:"",
										label:"Current Child Entries",
										labelwidth:170,
										padding:"20px",
										labeltextAlign:"center",
										valuetextAlign:"left",
									},	
									{
										rendertype:"kvrendertags",
										dbfield:"basictablechildren", //When we go to save the item, we'll need to have an idea of children!
										label:"Tags:",
										//It's possible we're making tags specifically for a Laptop... AssetType="Laptop"
										//Or perhaps Grade where this entry is for adding Grade A tags. If we want something more complicated, we'll have to make that.
										tagkey:"Name", //Human readable value/column - the thing we type in and change on the child row. "ConditionText"
										tagindex:"ID",
										defaultstyle:{borderRadius:3, color:"#FFF"},
										conditionalstyles:[
											{key:"Name", value:"A", backgroundColor:"#45B84A", color:"#FFF"},
											{key:"Name", value:"B", backgroundColor:"#CCB136", color:"#FFF"},
											{key:"Name", value:"C", backgroundColor:"#CE823E", color:"#FFF"},
											{key:null, color:"#FFF", backgroundColor:"#BBB"}
										],
										inline:true, //Allow items to show in-line or not.
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvheader",
										dbfield:"",
										label:"Add Child Entries",
										labelwidth:170,
										padding:"20px",
										labeltextAlign:"center",
										valuetextAlign:"left",
									},	
									{
										rendertype:"kvaddtags",
										dbfield:"basictablechildren", //When we go to save the item, we'll need to have an idea of children!
										label:"Add Tag:",
										//This is the human readable text tag we're adding. This particular one is mapped to child table column "Name".
										tagkey:"Name",
										//Next are default mappings. Here we can add values like "ProductID", "AssetType", "ConditionType" that help link tags to other tables or statues.
										defaultmappings:[
											{key:"Grade", value:"A"}

										], //Appendages to your data to be entered into the DB.
										inline:true, //Allow items to show in-line or not.
										placeholder:"Add tag and hit enter.",
										labelwidth:170,
										labeltextAlign:"right",
										valuetextAlign:"left",
									},
									{
										rendertype:"kvendbuttons",
										standardbuttons:{
											deletebutton:true,
											deletebuttonlabel:"Delete Item"
										},

									}
								]
							}
						]
					}
				]
			}
		]
	}


	/* ##########################  UseState Variables  ########################## */
	const [state, setState] = useState({
		dbreload: true,
		reloadparenttable: false, //New! So we need to change this every time we successfully save a record. 
		itemloaded: false,
		pendingupdate: false,
		editmode: configuration.defaulteditmode,
		deleteitem:false,
		auditview: false,
		historyavailable: (configuration.historysupport && configuration.auditPerm),
		itemdata: {

		},
		deleteitem:false //Used in SaveChanges
	});
	//Clone State! We'll get the view from localstate!
	let localstate = Object.assign({}, state);
	function UpdateState(stateobject) {
		setState(stateobject);
	}


	useEffect(() => {
		//To Do: On load of item, set title to the serial number.
		document.title = "Boilerplate Key Values";
		dispatch(setCurrentMenuSection(configuration.menusection));
		dispatch(setCurrentMenuItem(configuration.menuitem));
		if (state.dbreload) {
			//Avoid duplicate loads.
			localstate.dbreload = false;
			//Diagnostic:
			console.log("Localstate ID: " + id);
			LoadItem(localstate, UpdateState, configuration, dispatch);
		}
	}, [state]);


	return (
		<div style={{backgroundColor:"#EEE", borderLeft:"1px solid #AAA", minHeight:"calc(100vh - 35px)"}}>
			<FlexiItemTopMenu 
				localstate={localstate}
				UpdateState={UpdateState}
				classes={classes}
				configuration={configuration}
				SetView={SetView}
				isChild={isChild}
				backlinktext={backlinktext}
				dispatch={dispatch}
				history={history}
				perms={perms}
			/>
			<div key="itemcontainer" style={{
				margin:"auto", 
				maxWidth:configuration.maxwidth,
				textAlign:"center",
				fontFamily:"Inter",
				fontOpticalSizing:"auto",
				fontWeight:350,
				
				}}>
					{(localstate.itemloaded) &&
					<>
					<FlexiItemHeader headerprepend={configuration.headerprepend} headerappend={configuration.headerappend} headervalue={configuration.headervalue} localstate={localstate} />
					<FlexiItemContent configuration={configuration} localstate={localstate} UpdateState={UpdateState} classes={classes} perms={perms} dispatch={dispatch} />
					</>
						
					}
			</div>
		</div>
	);
}
export default BoilerplateFlexiItem;